export type FamilyKey = 'office' | 'workspace' | 'pdf' | 'scan' | 'pdfOnboarding'

export interface LineItemExtras {
  domain_users_text: string | null
  subscription_term_months: number | null
  monthly_delivery_day: number | null
  primary_contact_id: string | null
}

const RULES: Array<{ test: (n: string, f: string) => boolean; key: FamilyKey | null }> = [
  { test: (n) => /grackle pdf/i.test(n) && /onboarding/i.test(n), key: 'pdfOnboarding' },
  { test: (n, f) => f === 'Grackle Training' || /^\s*how to/i.test(n), key: null },
  { test: (n, f) => f === 'Grackle Scan' || /^\s*grackle scan/i.test(n), key: 'scan' },
  {
    test: (n, f) => f === 'Grackle Workspace' || /^\s*grackle workspace/i.test(n),
    key: 'workspace',
  },
  { test: (n) => /^\s*grackle office/i.test(n), key: 'office' },
  { test: (n, f) => f === 'Grackle PDF' || /^\s*grackle pdf/i.test(n), key: 'pdf' },
]

export function matchProductFamily(product: {
  name?: string | null
  family?: string | null
}): FamilyKey | null {
  const n = (product.name ?? '').toString()
  const f = (product.family ?? '').toString().trim()
  for (const r of RULES) if (r.test(n, f)) return r.key
  return null
}

export const REQUIRED_FIELDS_BY_FAMILY: Record<FamilyKey, ReadonlyArray<keyof LineItemExtras>> = {
  office: ['domain_users_text', 'subscription_term_months'],
  workspace: ['domain_users_text', 'subscription_term_months'],
  pdf: ['domain_users_text', 'subscription_term_months'],
  scan: ['domain_users_text', 'subscription_term_months', 'monthly_delivery_day'],
  pdfOnboarding: ['primary_contact_id'],
}

export const DOMAIN_USERS_LABEL_BY_FAMILY: Record<FamilyKey, string> = {
  office: 'Domain/User(s)',
  workspace: 'Domain/User(s)',
  pdf: 'User(s)',
  scan: 'Domain',
  pdfOnboarding: '',
}

export const SUBSCRIPTION_TERM_OPTIONS = [
  { value: 12, label: '1 Year' },
  { value: 24, label: '2 Years' },
  { value: 36, label: '3 Years' },
  { value: 60, label: '5 Years' },
] as const

export function validateLineItemExtras(
  product: { name?: string | null; family?: string | null },
  extras: Partial<LineItemExtras>
): { familyKey: FamilyKey | null; missing: Array<keyof LineItemExtras> } {
  const familyKey = matchProductFamily(product)
  if (!familyKey) return { familyKey: null, missing: [] }
  const required = REQUIRED_FIELDS_BY_FAMILY[familyKey]
  const missing = required.filter((f) => {
    const v = extras[f]
    return v === null || v === undefined || v === ''
  })
  return { familyKey, missing: [...missing] }
}

// Pragmatic email regex — rejects obvious junk while allowing common forms.
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

/** Split a comma-separated string into trimmed non-empty entries. */
export function parseEmailList(text: string | null | undefined): string[] {
  if (text === null || text === undefined) return []
  return String(text)
    .split(',')
    .map((e) => e.trim())
    .filter((e) => e.length > 0)
}

/** Validate a comma-separated email list; returns parsed entries and any invalid ones. */
export function validateEmailList(text: string | null | undefined): {
  emails: string[]
  invalid: string[]
} {
  const emails = parseEmailList(text)
  const invalid = emails.filter((e) => !EMAIL_RE.test(e))
  return { emails, invalid }
}

/**
 * For the 'pdf' family, `domain_users_text` must be a comma-separated list
 * of valid emails (one per user of the end product). Other families unchanged.
 */
export function validatePdfUserEmails(
  product: { name?: string | null; family?: string | null },
  extras: Partial<LineItemExtras>
): { applies: boolean; invalid: string[]; emailCount: number } {
  const familyKey = matchProductFamily(product)
  if (familyKey !== 'pdf') return { applies: false, invalid: [], emailCount: 0 }
  const { emails, invalid } = validateEmailList(extras.domain_users_text)
  return { applies: true, invalid, emailCount: emails.length }
}
